import dayjs from 'dayjs';

import { ApiFactory } from './Api';

const api = new ApiFactory();

/** @returns {number} YYYYMM or 0 if unknown */
function invoiceBillingYm(inv) {
    if (!inv) return 0;
    if (inv.billingMonth) {
        const compactBillingMonth = String(inv.billingMonth).replace(/\D/g, '');
        if (/^\d{6}$/.test(compactBillingMonth)) return parseInt(compactBillingMonth, 10);
    }
    if (inv.month) {
        const parts = String(inv.month).match(/\d+/g);
        if (parts?.length >= 2) {
            return parseInt(`${parts[0]}${parts[1].padStart(2, '0')}`, 10);
        }
        if (parts?.length === 1 && /^\d{6}$/.test(parts[0])) {
            return parseInt(parts[0], 10);
        }
    }
    const fallbackDate = inv.billingDate || inv.dateTo || inv.dateFrom;
    if (fallbackDate && dayjs(fallbackDate).isValid()) {
        return parseInt(dayjs(fallbackDate).format('YYYYMM'), 10);
    }
    return 0;
}

function mergeInvoiceLists(arrays) {
    const seen = new Set();
    const out = [];
    for (const arr of arrays) {
        for (const inv of arr || []) {
            if (!inv || seen.has(inv.id)) continue;
            seen.add(inv.id);
            out.push(inv);
        }
    }
    return out;
}

/**
 * Fetch invoices for a calendar month range. Uses per-year API calls (no month param) and filters by YYYYMM.
 */
async function getElectricInvoicesDateRange({ startYear, startMonth, endYear, endMonth, contractId, isContractGroup = false }) {
    const padM = (m) => (m ? String(m).padStart(2, '0') : '');

    const boundLo = (y, m) => {
        if (!y) return null;
        if (m) return parseInt(`${y}${padM(m)}`, 10);
        return parseInt(`${y}01`, 10);
    };
    const boundHi = (y, m) => {
        if (!y) return null;
        if (m) return parseInt(`${y}${padM(m)}`, 10);
        return parseInt(`${y}12`, 10);
    };

    let lo = boundLo(startYear, startMonth);
    let hi = boundHi(endYear, endMonth);

    if (lo != null && hi != null && lo > hi) {
        const t = lo;
        lo = hi;
        hi = t;
    }

    const filterRange = (invoices) => {
        let list = invoices || [];
        if (lo != null) list = list.filter((inv) => invoiceBillingYm(inv) >= lo);
        if (hi != null) list = list.filter((inv) => invoiceBillingYm(inv) <= hi);
        return list.sort((a, b) => invoiceBillingYm(b) - invoiceBillingYm(a));
    };

    if (!startYear && !endYear) {
        const all = await getElectricInvoices(undefined, undefined, contractId, isContractGroup);
        return filterRange(Array.isArray(all) ? all : []);
    }

    if (!startYear && endYear) {
        const all = await getElectricInvoices(undefined, undefined, contractId, isContractGroup);
        return filterRange(Array.isArray(all) ? all : []);
    }

    const currentYear = dayjs().year();
    const yearsToFetch = [];

    if (startYear && !endYear) {
        const ys = parseInt(startYear, 10);
        for (let y = ys; y <= currentYear; y++) yearsToFetch.push(y);
    } else if (startYear && endYear) {
        const ys = parseInt(startYear, 10);
        const ye = parseInt(endYear, 10);
        const a = Math.min(ys, ye);
        const b = Math.max(ys, ye);
        for (let y = a; y <= b; y++) yearsToFetch.push(y);
    }

    const batches = await Promise.all(yearsToFetch.map((y) => getElectricInvoices(String(y), undefined, contractId, isContractGroup)));
    return filterRange(mergeInvoiceLists(batches));
}

async function getBillingContracts() {
    const response = await api.get(`/electric/contracts/billing`);
    return response.data;
}

async function getElectricInvoices(year, month, contractId, isContractGroup = false, invoiceType) {
    const queryParams = [];

    if (contractId) {
        queryParams.push(`contract_id=${contractId}`);
    }
    if (year) {
        queryParams.push(`year=${year}`);
    }
    if (month) {
        queryParams.push(`month=${month}`);
    }
    queryParams.push(`is_contract_group=${isContractGroup}`);

    if (invoiceType) {
        queryParams.push(`invoice_type=${invoiceType}`);
    }

    const url = `/electric/invoices${queryParams.length ? `?${queryParams.join('&')}` : ''}`;

    const response = await api.get(url);
    return response.data;
}

async function downloadInvoice(invoiceId) {
    const response = await api.post(`/electric/${invoiceId}/download`);
    return response.data;
}

async function getElectricUsageInvoices(contractId) {
    const response = await api.get(`/electric/contracts/${contractId}/usage`);
    return response.data;
}

async function getElectricUsageDaily(request) {
    const response = await api.get(`/electric/usage/daily`, request);
    return response.data;
}

async function getElectricUsageMonthly(request) {
    const response = await api.get(`/electric/usage/monthly`, request);
    return response.data;
}

async function downloadAllInvoices({ invoiceIds }) {
    const response = await api.post('/electric/download-zip', { invoiceIds }, null, { responseType: 'blob' });
    const blob = new Blob([response], { type: 'application/zip' });
    const url = window.URL.createObjectURL(blob);

    return { url };
}

async function getContracts(page, pageSize, onlyDr = false, includeDocuments = false) {
    console.log(includeDocuments)
    const response = await api.get(`/electric/contracts`, { page, pageSize, onlyDr, includeDocuments });
    return response.data;
}

async function getContractDetail(id) {
    const response = await api.get(`/electric/contracts/${id}`);
    return response.data;
}

async function getInvoice(invoiceId) {
    const response = await api.get(`/electric/invoices/${invoiceId}`);
    return response.data;
}

async function updateDrValidFlag(contractId, drValidFlag) {
    const response = await api.post(`/electric/contracts/dr-valid-flag`, {
        contractId,
        drValidFlag
    });
    return response.data;
}

async function getUsageContracts() {
    const response = await api.get(`/electric/contracts/usage`);
    return response.data;
}

async function getElectricUsageSummary() {
    const response = await api.get(`/electric/usage/summary`);
    return response.data;
}

async function getElectricUsageYearly(contractId) {
    const response = await api.get(`/electric/usage/yearly?contract_id=${contractId}`);
    return response.data;
}

async function getElectricTaxDetail(contractId, billingMonth) {
    const response = await api.get(`/electric/tax-detail?contract_id=${contractId}&billing_month=${billingMonth}`);
    return response.data;
}

async function getInvoiceDetailByPeriod(contractId, year, month) {
    const queryParams = [];
    if (contractId) queryParams.push(`contract_id=${contractId}`);
    if (year) queryParams.push(`year=${year}`);
    if (month) queryParams.push(`month=${month}`);

    const url = `/electric/invoices/period-detail${queryParams.length ? `?${queryParams.join('&')}` : ''}`;

    const response = await api.get(url);
    if (response?.data && typeof response.data === 'object' && 'data' in response.data) {
        return response.data.data;
    }
    return response.data;
}

async function exportElectricUsageExcel({ startDate, endDate, supplyIds }) {
    const params = {
        start_date: startDate,
        end_date: endDate,
        supply_ids: supplyIds
    };

    const queryString = new URLSearchParams(params).toString();
    const url = `/electric/usage/export?${queryString}`;

    const response = await api.get(url);
    const bufferData = response.data?.data || response.data;

    if (!bufferData || !Array.isArray(bufferData)) {
        throw new Error('Invalid data format');
    }

    const byteArray = new Uint8Array(bufferData);

    const blob = new Blob([byteArray], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    const urlBlob = window.URL.createObjectURL(blob);

    return {
        url: urlBlob,
        fileName: response.fileName
    };
}

export {
    downloadAllInvoices,
    downloadInvoice,
    getBillingContracts,
    getContractDetail,
    getContracts,
    getElectricInvoices,
    getElectricInvoicesDateRange,
    getElectricTaxDetail,
    getElectricUsageDaily,
    getElectricUsageInvoices,
    getElectricUsageMonthly,
    getElectricUsageSummary,
    getElectricUsageYearly,
    getInvoice,
    getInvoiceDetailByPeriod,
    getUsageContracts,
    updateDrValidFlag,
    exportElectricUsageExcel
};
